import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Space,
  Typography,
} from 'antd';

import { IntegrationSteps } from '@app/pages/Integrations/IntegrationsList/components/Add1CIntegrationModal/ui';

import { useAdd1CIntegrationModal } from '@pages/Integrations/IntegrationsList/components/Add1CIntegrationModal/hooks';

import type { FC } from 'react';

import '@pages/Integrations/IntegrationsList/components/Add1CIntegrationModal/styles/Add1CIntegrationModal.desktop.scss';

export interface Add1CIntegrationModalProps {
  isOpen: boolean;
  onCancel(): void;
  afterSave?(): void;
}

const { Title, Text, Paragraph } = Typography;
const { Item } = Form;
const { Password } = Input;

export const Add1CIntegrationModal: FC<Add1CIntegrationModalProps> = ({
  isOpen,
  onCancel,
  afterSave,
}) => {
  const {
    form,
    step,
    connectionError,
    warehousesList,
    selectedWarehouseIds,
    isPrimaryActionLoading,
    isPrimaryActionDisabled,
    isSaveValidationVisible,
    handleReset,
    handlePrimaryAction,
    handleToggleWarehouse,
    handleContinue,
  } = useAdd1CIntegrationModal({ isOpen, onCancel, afterSave });

  return (
    <Modal
      open={isOpen}
      onCancel={onCancel}
      footer={null}
      width={560}
      destroyOnHidden
      centered
      className="Add1CIntegrationModal"
      afterClose={handleReset}
      title={
        <div className="Add1CIntegrationModal__title">
          <Title level={5} className="Add1CIntegrationModal__titleText">
            Создание интеграции &quot;1С Склад&quot;
          </Title>
        </div>
      }
    >
      <div className="Add1CIntegrationModal__content">
        <IntegrationSteps
          activeStepIndex={step === 'auth' ? 0 : 1}
          steps={[{ title: 'Авторизация' }, { title: 'Настройки' }]}
        />

        {step === 'auth' ? (
          <div className="Add1CIntegrationModal__stepContent">
            <Form
              form={form}
              layout="vertical"
              requiredMark={false}
              className="Add1CIntegrationModal__form"
              onFinish={handleContinue}
            >
              <Item
                label="Название"
                name="integrationName"
                initialValue="1С Склад"
                rules={[{ required: true, message: 'Введите название' }]}
              >
                <Input
                  placeholder="Введите название интеграции"
                  autoComplete="off"
                />
              </Item>
              <Item
                label="Адрес сервиса"
                name="serviceUrl"
                rules={[
                  { required: true, message: 'Введите адрес сервиса' },
                  {
                    validator: async (_, value?: string) => {
                      if (!value) return;
                      try {
                        new URL(value);
                      } catch {
                        return Promise.reject(new Error('Некорректный URL'));
                      }
                    },
                  },
                ]}
              >
                <Input
                  placeholder="https://example-1c.company.ru"
                  autoComplete="off"
                  inputMode="url"
                />
              </Item>

              <Item
                label="Логин"
                name="login"
                rules={[{ required: true, message: 'Введите логин' }]}
              >
                <Input placeholder="Введите логин" autoComplete="off" />
              </Item>

              <Item
                label="Пароль"
                name="password"
                rules={[{ required: true, message: 'Введите пароль' }]}
                validateStatus={connectionError ? 'error' : undefined}
              >
                <Password placeholder="Введите пароль" autoComplete="off" />
              </Item>

              {!!connectionError && (
                <Alert
                  type="error"
                  showIcon={false}
                  className="Add1CIntegrationModal__connectionAlert"
                  message={connectionError}
                />
              )}
            </Form>
          </div>
        ) : (
          <div className="Add1CIntegrationModal__stepContent">
            <Text className="Add1CIntegrationModal__sectionTitle">
              Выберите склады
            </Text>
            <Paragraph className="Add1CIntegrationModal__sectionHint">
              Отметьте склады, синхронизируемые в фоновом режиме.
            </Paragraph>

            <div className="Add1CIntegrationModal__warehouses">
              <Space
                direction="vertical"
                size={8}
                className="Add1CIntegrationModal__warehousesList"
              >
                {warehousesList.map(({ id, name }) => (
                  <Checkbox
                    key={id}
                    checked={selectedWarehouseIds.includes(id)}
                    onChange={({ target: { checked } }) => {
                      handleToggleWarehouse(id, checked);
                    }}
                  >
                    {name}
                  </Checkbox>
                ))}
              </Space>
            </div>

            {isSaveValidationVisible && (
              <Text
                className="Add1CIntegrationModal__saveValidation"
                type="danger"
              >
                Выберите хотя бы один склад.
              </Text>
            )}
          </div>
        )}
      </div>

      <div className="Add1CIntegrationModal__footer">
        <Button onClick={onCancel} disabled={isPrimaryActionLoading}>
          Отменить
        </Button>

        <Button
          type="primary"
          onClick={handlePrimaryAction}
          loading={isPrimaryActionLoading}
          disabled={isPrimaryActionDisabled}
        >
          {step === 'auth' ? 'Продолжить' : 'Сохранить'}
        </Button>
      </div>
    </Modal>
  );
};
